<template>
  <div class="bulk-channel-selector">
    <div class="mb-6">
      <h4 class="text-lg font-medium text-slate-900 dark:text-white mb-2">
        Selecciona el canal de comunicación
      </h4>
      <p class="text-sm text-slate-600 dark:text-slate-400">
        Elige el canal que deseas usar para enviar tu mensaje masivo
      </p>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <!-- WhatsApp -->
      <button
        @click="selectChannel('whatsapp')"
        :class="[
          'p-6 border rounded-lg text-left transition-all group',
          selectedChannel === 'whatsapp'
            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
            : 'border-slate-200 dark:border-slate-700 hover:border-green-300 hover:bg-green-50/50 dark:hover:bg-green-900/10'
        ]"
      >
        <div class="flex items-start space-x-3">
          <div
            :class="[
              'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center',
              selectedChannel === 'whatsapp'
                ? 'bg-green-500 text-white'
                : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
            ]"
          >
            <Icon icon="i-lucide-message-circle" size="24" />
          </div>
          <div class="flex-grow min-w-0">
            <h5
              :class="[
                'font-medium',
                selectedChannel === 'whatsapp'
                  ? 'text-green-900 dark:text-green-100'
                  : 'text-slate-900 dark:text-white group-hover:text-green-700 dark:group-hover:text-green-300'
              ]"
            >
              WhatsApp
            </h5>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Envía mensajes usando plantillas aprobadas de WhatsApp
            </p>
            <div class="mt-2">
              <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300">
                Plantillas soportadas
              </span>
            </div>
          </div>
        </div>
      </button>

      <!-- Email -->
      <button
        @click="selectChannel('email')"
        :class="[
          'p-6 border rounded-lg text-left transition-all group',
          selectedChannel === 'email'
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-slate-200 dark:border-slate-700 hover:border-blue-300 hover:bg-blue-50/50 dark:hover:bg-blue-900/10'
        ]"
      >
        <div class="flex items-start space-x-3">
          <div
            :class="[
              'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center',
              selectedChannel === 'email'
                ? 'bg-blue-500 text-white'
                : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
            ]"
          >
            <Icon icon="i-lucide-mail" size="24" />
          </div>
          <div class="flex-grow min-w-0">
            <h5
              :class="[
                'font-medium',
                selectedChannel === 'email'
                  ? 'text-blue-900 dark:text-blue-100'
                  : 'text-slate-900 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300'
              ]"
            >
              Email
            </h5>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Envía campañas de email personalizadas
            </p>
            <div class="mt-2">
              <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                Próximamente
              </span>
            </div>
          </div>
        </div>
      </button>

      <!-- SMS -->
      <button
        @click="selectChannel('sms')"
        :class="[
          'p-6 border rounded-lg text-left transition-all group',
          selectedChannel === 'sms'
            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
            : 'border-slate-200 dark:border-slate-700 hover:border-purple-300 hover:bg-purple-50/50 dark:hover:bg-purple-900/10'
        ]"
      >
        <div class="flex items-start space-x-3">
          <div
            :class="[
              'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center',
              selectedChannel === 'sms'
                ? 'bg-purple-500 text-white'
                : 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
            ]"
          >
            <Icon icon="i-lucide-smartphone" size="24" />
          </div>
          <div class="flex-grow min-w-0">
            <h5
              :class="[
                'font-medium',
                selectedChannel === 'sms'
                  ? 'text-purple-900 dark:text-purple-100'
                  : 'text-slate-900 dark:text-white group-hover:text-purple-700 dark:group-hover:text-purple-300'
              ]"
            >
              SMS
            </h5>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Envía mensajes de texto directamente
            </p>
            <div class="mt-2">
              <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                Próximamente
              </span>
            </div>
          </div>
        </div>
      </button>
    </div>

    <!-- WhatsApp Inbox Selector -->
    <WhatsAppInboxSelector
      v-if="selectedChannel === 'whatsapp'"
      :selected-inbox="selectedInbox"
      @update:selected-inbox="handleInboxChange"
    />

    <!-- Selected Channel Info -->
    <div v-if="selectedChannel" class="mt-8 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
      <div class="flex items-start space-x-3">
        <Icon icon="i-lucide-info" class="text-blue-500 mt-0.5" size="20" />
        <div>
          <h6 class="font-medium text-slate-900 dark:text-white">
            Canal seleccionado
          </h6>
          <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {{ getChannelInfoText(selectedChannel) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import WhatsAppInboxSelector from './WhatsAppInboxSelector.vue';

const props = defineProps({
  selectedChannel: {
    type: String,
    default: null
  },
  selectedInbox: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['update:selectedChannel', 'update:selectedInbox']);

const { t } = useI18n();

const selectChannel = (channel) => {
  emit('update:selectedChannel', channel);
  // Reset inbox selection when channel changes
  if (channel !== 'whatsapp') {
    emit('update:selectedInbox', null);
  }
};

const handleInboxChange = (inbox) => {
  emit('update:selectedInbox', inbox);
};

const getChannelInfoText = (channel) => {
  const infoTexts = {
    whatsapp: 'WhatsApp te permite enviar mensajes usando plantillas pre-aprobadas con variables personalizables.',
    email: 'Email permite enviar campañas personalizadas con contenido HTML y adjuntos.',
    sms: 'SMS envía mensajes de texto cortos directamente al teléfono del destinatario.'
  };
  
  return infoTexts[channel] || '';
};
</script>